import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, ArrowsClockwise, MagnifyingGlass, Spinner, PencilSimple } from '@phosphor-icons/react';
import axios, { API } from '../../services/api';

const FREQUENCY_OPTIONS = [
  { key: 'once_daily', label: 'Once daily' },
  { key: 'twice_daily', label: 'Twice daily' },
  { key: 'three_daily', label: 'Three times daily' },
  { key: 'morning', label: 'Morning' },
  { key: 'night', label: 'Night' },
  { key: 'as_needed', label: 'As needed' },
  { key: 'weekly', label: 'Weekly' },
];

// P2 Stage-14b — common-medication chips so a brand-new user is never
// staring at a bare search box. Mix of mental-health / women's health /
// metabolic / supplements so the strip doesn't read as a single specialty.
// Tap to add with a sensible default frequency; user can edit after.
const COMMON_MED_CHIPS = [
  { name: 'Sertraline',      frequency: 'once_daily' },
  { name: 'Escitalopram',    frequency: 'once_daily' },
  { name: 'Vyvanse',         frequency: 'morning' },
  { name: 'Levothyroxine',   frequency: 'morning' },
  { name: 'Metformin',       frequency: 'twice_daily' },
  { name: 'Ozempic',         frequency: 'weekly' },
  { name: 'The pill',        frequency: 'once_daily' },
  { name: 'HRT',             frequency: 'once_daily' },
  { name: 'Vitamin D',       frequency: 'once_daily' },
  { name: 'Iron',            frequency: 'once_daily' },
];

/* ── Medication Autocomplete ── */
const MedAutocomplete = ({ value, onChange, onSelectItem }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(!!value);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) { setResults([]); setShowDropdown(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/medications/search?q=${encodeURIComponent(q)}&limit=8`);
        setResults(data);
        setShowDropdown(data.length > 0);
      } catch { setResults([]); }
      setSearching(false);
    }, 200);
  }, []);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setQuery(v); setSelected(false); onChange(v); handleSearch(v);
  };

  const handleSelect = (item) => {
    const name = item.type === 'brand' ? item.display : item.name;
    setQuery(name); setSelected(true); setShowDropdown(false); onChange(name);
    if (onSelectItem) onSelectItem(item);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={query} onChange={handleInputChange}
          onFocus={() => { if (results.length > 0 && !selected) setShowDropdown(true); }}
          placeholder="Search medication or supplement..."
          className="w-full bg-white border border-slate-200 rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20"
          data-testid="onboarding-med-name" autoComplete="off" autoFocus />
        {searching && <Spinner size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
      </div>
      <AnimatePresence>
        {showDropdown && results.length > 0 && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-auto"
            data-testid="onboarding-med-search-results">
            {results.map((item, i) => (
              <button key={i} onClick={() => handleSelect(item)}
                className="w-full px-4 py-2.5 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0 flex items-center justify-between"
                data-testid={`onboarding-med-result-${i}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{item.display}</p>
                  <p className="text-[10px] text-slate-400 capitalize">{item.category?.replace('_', ' ')}</p>
                </div>
                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
                  item.type === 'brand' ? 'bg-blue-50 text-blue-500' : 'bg-slate-100 text-slate-500'
                }`}>{item.type}</span>
              </button>
            ))}
            <div className="px-4 py-2 bg-slate-50 text-[10px] text-slate-400">
              Can't find yours? Type the full name and add it manually.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Frequency Picker ── */
const FrequencyPicker = ({ value, onChange }) => {
  const isCustom = value && !FREQUENCY_OPTIONS.some(o => o.key === value);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {FREQUENCY_OPTIONS.map(({ key, label }) => (
          <button key={key} onClick={() => onChange(key)} type="button"
            className={`px-3 py-1.5 rounded-full text-xs transition-colors border ${
              value === key ? 'bg-[#235347] text-white border-[#235347]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
            }`} data-testid={`freq-${key}`}>
            {label}
          </button>
        ))}
        <button onClick={() => onChange('custom')} type="button"
          className={`px-3 py-1.5 rounded-full text-xs transition-colors border ${
            isCustom ? 'bg-[#235347] text-white border-[#235347]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
          }`}>
          Custom
        </button>
      </div>
      {(isCustom || value === 'custom') && (
        <input value={isCustom ? value : ''} onChange={(e) => onChange(e.target.value || 'custom')}
          placeholder="e.g. Every other day, MWF..."
          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
          data-testid="freq-custom-input" autoFocus />
      )}
    </div>
  );
};

/* ── Main Component ── */
export const OnboardingMedications = ({ data, onChange }) => {
  const { medications = [] } = data;
  const [draft, setDraft] = useState({ name: '', dosage: '', frequency: '', purpose: '', recent_change: false });
  const [editingIdx, setEditingIdx] = useState(null);

  const addMedication = () => {
    if (!draft.name.trim()) return;
    onChange({ ...data, medications: [...medications, { ...draft, name: draft.name.trim() }] });
    setDraft({ name: '', dosage: '', frequency: '', purpose: '', recent_change: false });
  };

  const removeMedication = (index) => {
    setEditingIdx(null);
    onChange({ ...data, medications: medications.filter((_, i) => i !== index) });
  };

  const updateMedication = (index, field, value) => {
    const updated = [...medications];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...data, medications: updated });
  };

  const handleMedSelect = (item) => {
    if (item.forms && item.forms.length > 0 && !draft.dosage) {
      setDraft(d => ({ ...d, dosage: item.forms[0] }));
    }
  };

  const freqLabel = (key) => {
    const opt = FREQUENCY_OPTIONS.find(o => o.key === key);
    return opt ? opt.label : key?.replace(/_/g, ' ');
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="bg-white border border-slate-150 rounded-2xl p-5">
        <p className="text-slate-700 leading-relaxed">
          Are you currently taking any medications or supplements?
        </p>
        <p className="text-sm text-slate-400 mt-2">
          Include dosage and how often if you know.
        </p>
      </div>

      {/* P2 Stage-14b — Quick-add chips. Hidden once a few meds are added so
          the strip doesn't keep dominating the screen. */}
      {medications.length < 3 && (
        <div data-testid="onboarding-med-chips">
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400 mb-2">
            Common — tap to add
          </p>
          <div className="flex flex-wrap gap-1.5">
            {COMMON_MED_CHIPS
              .filter((c) => !medications.some((m) => m.name?.toLowerCase() === c.name.toLowerCase()))
              .map((chip) => (
                <button
                  key={chip.name}
                  type="button"
                  onClick={() => onChange({ ...data, medications: [...medications, { name: chip.name, dosage: '', frequency: chip.frequency, purpose: '', recent_change: false }] })}
                  className="text-[12.5px] text-[#235347] bg-[#235347]/8 border border-[#235347]/15 rounded-full px-3 py-1.5 hover:bg-[#235347]/14 transition-colors flex items-center gap-1"
                  data-testid={`onboarding-med-chip-${chip.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Plus size={11} weight="bold" />
                  {chip.name}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Added medications with edit */}
      <AnimatePresence>
        {medications.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            {medications.map((med, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white border border-slate-200 rounded-xl p-4" data-testid="onboarding-med-item">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {med.name}
                      {med.dosage && <span className="text-slate-500 font-normal ml-1">· {med.dosage}</span>}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {med.frequency && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{freqLabel(med.frequency)}</span>
                      )}
                      {med.purpose && <span className="text-xs text-slate-400">For {med.purpose}</span>}
                      {med.recent_change && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600">
                          <ArrowsClockwise size={10} /> Changed
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setEditingIdx(editingIdx === idx ? null : idx)}
                      className="text-slate-300 hover:text-[#235347] p-1" data-testid={`edit-med-${idx}`}>
                      <PencilSimple size={14} />
                    </button>
                    <button onClick={() => removeMedication(idx)}
                      className="text-slate-300 hover:text-red-500 p-1" data-testid="onboarding-med-remove">
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {/* Inline edit */}
                <AnimatePresence>
                  {editingIdx === idx && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-400 font-medium mb-1 block">Dosage</label>
                            <input value={med.dosage || ''} onChange={(e) => updateMedication(idx, 'dosage', e.target.value)}
                              placeholder="e.g. 10mg" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none" />
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-400 font-medium mb-1 block">Purpose</label>
                            <input value={med.purpose || ''} onChange={(e) => updateMedication(idx, 'purpose', e.target.value)}
                              placeholder="e.g. anxiety" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none" />
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-400 font-medium mb-1.5 block">Frequency</label>
                          <FrequencyPicker value={med.frequency || ''} onChange={(v) => updateMedication(idx, 'frequency', v)} />
                        </div>
                        <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                          <input type="checkbox" checked={med.recent_change || false}
                            onChange={(e) => updateMedication(idx, 'recent_change', e.target.checked)}
                            className="rounded border-slate-300 text-[#235347] focus:ring-[#235347]" />
                          Recently changed
                        </label>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add medication form */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
        <MedAutocomplete value={draft.name} onChange={(name) => setDraft(d => ({ ...d, name }))} onSelectItem={handleMedSelect} />
        <div className="flex gap-3">
          <input data-testid="onboarding-med-dosage" placeholder="Dosage (e.g. 10mg)" value={draft.dosage}
            onChange={(e) => setDraft(d => ({ ...d, dosage: e.target.value }))}
            className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none" />
          <input data-testid="onboarding-med-purpose" placeholder="Purpose (e.g. anxiety)" value={draft.purpose}
            onChange={(e) => setDraft(d => ({ ...d, purpose: e.target.value }))}
            className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none" />
        </div>
        <div>
          <label className="text-[10px] text-slate-400 font-medium mb-1.5 block">How often?</label>
          <FrequencyPicker value={draft.frequency} onChange={(v) => setDraft(d => ({ ...d, frequency: v }))} />
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer">
            <input type="checkbox" checked={draft.recent_change}
              onChange={(e) => setDraft(d => ({ ...d, recent_change: e.target.checked }))}
              className="rounded border-slate-300 text-[#235347] focus:ring-[#235347]" />
            Recently changed
          </label>
          <button data-testid="onboarding-med-add" onClick={addMedication} disabled={!draft.name.trim()}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#235347] text-white rounded-lg text-sm font-medium disabled:opacity-40 hover:bg-[#1A3F36] transition-colors">
            <Plus size={14} weight="bold" /> Add
          </button>
        </div>
      </div>
    </motion.div>
  );
};
